import { modeLabel, MODE_POST, MODE_CP } from "@/lib/businessModel";
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Plus, Users, Wallet, Boxes, ShoppingCart, Banknote, ListChecks, CheckCircle2, XCircle, Pencil, FileText, Upload, RefreshCw, Trash2, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import adminApi from "@/lib/adminApi";
import { PortalAccessManager } from "@/components/PortalAccess";
import { CorrectSaleDialog } from "@/components/CorrectSaleDialog";
import { EditInventoryDialog } from "@/components/EditInventoryDialog";
import { DeleteDeviceDialog } from "@/components/DeleteDeviceDialog";
import { DevicePhotos } from "@/components/DevicePhotos";
import { DeviceDialog } from "@/components/NewIPhonesTab";

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("es", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fdate = (s) => { if (!s) return "—"; try { const hasTime = typeof s === "string" && s.length > 10; const d = new Date(hasTime ? s : s + "T00:00:00"); const b = `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`; return hasTime ? `${b} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}` : b; } catch { return s; } };
const MODE_LABEL = { FIXED_OWNER_PRICE: "Precio fijo", COST_PLUS_PROFIT_SHARE: "Capital + participación" };
const MODES = [
  ["FIXED_OWNER_PRICE", "Precio fijo para el consignador", "El dueño define cuánto debe recibir por cada equipo.", "Álamo"],
  ["COST_PLUS_PROFIT_SHARE", "Capital + participación en ganancias", "El dueño recupera su capital y recibe un porcentaje de la utilidad de la venta.", "Jonathan"],
];
const STATUS_BADGE = { DISPONIBLE: "bg-emerald-100 text-emerald-700", RESERVADO: "bg-amber-100 text-amber-700", VENDIDO: "bg-zinc-200 text-zinc-700" };
const normTxt = (s) => (s == null ? "" : String(s).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, ""));
const PAY_BADGE = { PENDIENTE: "bg-amber-100 text-amber-700", CONFIRMADO: "bg-emerald-100 text-emerald-700", CANCELADO: "bg-zinc-200 text-zinc-500" };

const RECEIPT_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp", "application/pdf"];
const RECEIPT_ACCEPT = "image/jpeg,image/png,image/webp,application/pdf";
const MAX_RECEIPT_BYTES = 5 * 1024 * 1024;
const fileToData = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
const validateReceipt = (file) => {
  if (!file) return false;
  if (!RECEIPT_TYPES.includes(file.type)) { toast.error("Tipo no permitido. Usa JPG, PNG, WEBP o PDF"); return false; }
  if (file.size > MAX_RECEIPT_BYTES) { toast.error("El archivo supera el límite de 5 MB"); return false; }
  return true;
};
const openPaymentReceipt = async (pid) => {
  const w = window.open("", "_blank");
  try {
    const r = await adminApi.get(`/admin/payments/${pid}/receipt`);
    const data = r.data.receipt;
    if (!data) { if (w) w.close(); toast.error("Sin comprobante"); return; }
    const isData = typeof data === "string" && data.startsWith("data:");
    const url = isData ? URL.createObjectURL(await (await fetch(data)).blob()) : data;
    if (w) w.location.href = url; else window.open(url, "_blank");
    if (isData) setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch { if (w) w.close(); toast.error("Sin comprobante"); }
};

function OwnerFormDialog({ owner, onClose, onSaved }) {
  const editing = !!owner;
  const [name, setName] = useState(owner?.name || "");
  const [code, setCode] = useState(owner?.code || "");
  const [mode, setMode] = useState(owner?.settlement_mode === "COST_PLUS_PROFIT_SHARE" ? "COST_PLUS_PROFIT_SHARE" : "FIXED_OWNER_PRICE");
  const [pct, setPct] = useState(owner?.profit_share_pct != null ? String(owner.profit_share_pct) : "50");
  const [saving, setSaving] = useState(false);
  const cp = mode === "COST_PLUS_PROFIT_SHARE";
  const save = async () => {
    if (!name.trim() || !code.trim()) { toast.error("Nombre y código requeridos"); return; }
    if (cp && !(Number(pct) > 0 && Number(pct) <= 100)) { toast.error("El porcentaje debe ser > 0 y <= 100"); return; }
    setSaving(true);
    try {
      const body = { name: name.trim(), code: code.trim(), settlement_mode: mode, profit_share_pct: cp ? Number(pct) : null };
      if (editing) await adminApi.put(`/admin/owners/${owner.id}`, body);
      else await adminApi.post("/admin/owners", body);
      toast.success(editing ? "Consignador actualizado" : "Consignador creado"); onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="cons-form-dialog">
        <DialogHeader><DialogTitle>{editing ? `Editar · ${owner.name}` : "Nuevo consignador"}</DialogTitle>
          <DialogDescription>Cambiar la modalidad no recalcula ventas históricas; aplica a operaciones futuras.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input placeholder="Nombre" value={name} onChange={(e) => setName(e.target.value)} className="h-11 rounded-xl flex-1" data-testid="cons-form-name" />
            <Input placeholder="Código" value={code} onChange={(e) => setCode(e.target.value)} className="h-11 w-28 rounded-xl text-center font-mono" data-testid="cons-form-code" />
          </div>
          <span className="text-[10px] uppercase tracking-wider text-zinc-400">Tipo de acuerdo</span>
          <div className="grid gap-2">
            {MODES.map(([val, title, desc, refName]) => (
              <button key={val} type="button" onClick={() => setMode(val)} data-testid={`cons-form-mode-${val}`}
                className={`text-left rounded-xl border p-2.5 transition-colors ${mode === val ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 hover:border-zinc-300"}`}>
                <div className="flex items-center gap-2">
                  <span className={`h-3.5 w-3.5 rounded-full border-2 shrink-0 ${mode === val ? "border-zinc-900 bg-zinc-900" : "border-zinc-300"}`} />
                  <span className="text-xs font-semibold text-zinc-800">{title}</span>
                </div>
                <p className="text-[11px] text-zinc-500 mt-1 ml-5">{desc}</p>
                <p className="text-[10px] text-zinc-400 mt-0.5 ml-5">Modelo de referencia: {refName}</p>
              </button>
            ))}
          </div>
          {cp && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-500">Porcentaje de utilidad para el consignador</span>
              <Input type="number" min="1" max="100" value={pct} onChange={(e) => setPct(e.target.value)} className="h-9 w-16 rounded-lg text-xs text-center" data-testid="cons-form-pct" />
              <span className="text-xs text-zinc-400">%</span>
            </div>
          )}
          {editing && <PortalAccessManager kind="consignador" refId={owner.id} name={owner.name} />}
          <Button onClick={save} disabled={saving} className="h-11 w-full rounded-xl bg-zinc-900 hover:bg-zinc-800" data-testid="cons-form-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? "Guardar cambios" : "Crear consignador")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-3 min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-zinc-400 leading-tight">{label}</p>
      <p className={`text-base font-bold mt-1 break-words ${accent || "text-zinc-900"}`}>{value}</p>
    </div>
  );
}

function PostSaleSettleDialog({ item, onClose, onSaved }) {
  const s = item.sale || {};
  const sold = s.actual_sale_price ?? s.sale_price ?? item.sale_price ?? 0;
  const [ownerAmt, setOwnerAmt] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const oa = Number(ownerAmt);
  const valid = ownerAmt !== "" && oa >= 0 && oa <= Number(sold) + 1e-6;
  const pc = valid ? Math.round((Number(sold) - oa) * 100) / 100 : null;
  const save = async () => {
    if (!valid) { toast.error("El monto debe estar entre 0 y el precio vendido"); return; }
    setSaving(true);
    try { await adminApi.post(`/admin/new-iphones/${item.id}/post-sale-settlement`, { owner_amount: oa, note: note || null }); toast.success("Liquidación definida"); onSaved?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="cons-settle-dialog">
        <DialogHeader><DialogTitle>Definir liquidación · {item.model} {item.capacity}</DialogTitle>
          <DialogDescription>Reparte el precio vendido entre el consignador y PuntoCell. Se acredita una sola vez.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="flex justify-between text-sm"><span className="text-zinc-500">Precio real vendido</span><span className="font-bold" data-testid="cons-settle-sold">{money(sold)}</span></div>
          <label className="flex flex-col gap-0.5"><span className="text-[11px] text-zinc-500 px-1">Monto para el consignador *</span>
            <Input data-testid="cons-settle-owner" type="number" min="0" value={ownerAmt} onChange={(e) => setOwnerAmt(e.target.value)} className="h-10 rounded-xl" /></label>
          <div className="flex justify-between text-sm"><span className="text-zinc-500">Para PuntoCell</span><span className="font-semibold" data-testid="cons-settle-pc">{pc == null ? "—" : money(pc)}</span></div>
          <Input data-testid="cons-settle-note" placeholder="Nota del acuerdo (opcional)" value={note} onChange={(e) => setNote(e.target.value)} className="h-10 rounded-xl" />
          <Button onClick={save} disabled={!valid || saving} className="h-11 w-full rounded-xl bg-zinc-900 hover:bg-zinc-800" data-testid="cons-settle-confirm">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar liquidación"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PayRow({ p, onConfirm, onCancel, onAttach }) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-3 flex items-center justify-between gap-2" data-testid={`cons-pay-${p.id}`}>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-zinc-900">{money(p.amount)} <span className="font-normal text-zinc-400">· {p.method}</span></p>
        <p className="text-[11px] text-zinc-500">{p.id} · {fdate(p.created_at)}{p.confirmation?.reference ? ` · Ref ${p.confirmation.reference}` : ""}</p>
        {p.has_receipt && p.receipt?.attached_by && <p className="text-[11px] text-zinc-400 flex items-center gap-1" data-testid={`cons-pay-receipt-meta-${p.id}`}><FileText className="w-3 h-3" /> Comprobante por {p.receipt.attached_by} · {fdate(p.receipt.attached_at)}</p>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${PAY_BADGE[p.status] || "bg-zinc-100"}`}>{p.status}</span>
        {p.status !== "CANCELADO" && p.has_receipt && (
          <button onClick={() => openPaymentReceipt(p.id)} className="text-emerald-700 hover:text-emerald-800" title="Ver comprobante" data-testid={`cons-pay-view-receipt-${p.id}`}><FileText className="w-5 h-5" /></button>
        )}
        {p.status !== "CANCELADO" && (
          <label className="text-zinc-500 hover:text-zinc-700 cursor-pointer" title={p.has_receipt ? "Reemplazar comprobante" : "Adjuntar comprobante"} data-testid={`cons-pay-attach-${p.id}`}>
            {p.has_receipt ? <RefreshCw className="w-5 h-5" /> : <Upload className="w-5 h-5" />}
            <input type="file" accept={RECEIPT_ACCEPT} onChange={(e) => { const f = e.target.files?.[0]; if (f) onAttach(p, f); e.target.value = ""; }} className="hidden" />
          </label>
        )}
        {p.status === "PENDIENTE" && (
          <>
            <button onClick={() => onConfirm(p)} className="text-emerald-600 hover:text-emerald-700" title="Confirmar" data-testid={`cons-pay-confirm-${p.id}`}><CheckCircle2 className="w-5 h-5" /></button>
            <button onClick={() => onCancel(p)} className="text-rose-500 hover:text-rose-600" title="Cancelar" data-testid={`cons-pay-cancel-${p.id}`}><XCircle className="w-5 h-5" /></button>
          </>
        )}
      </div>
    </div>
  );
}

function OwnerDetail({ owner, onBack, onEdit }) {
  const [sub, setSub] = useState("resumen");
  const [summary, setSummary] = useState(null);
  const [account, setAccount] = useState(null);
  const [usedItems, setUsedItems] = useState([]);
  const [payments, setPayments] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payOpen, setPayOpen] = useState(false);
  const [correcting, setCorrecting] = useState(null);
  const [settling, setSettling] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [ownersList, setOwnersList] = useState([]);
  const [editDevice, setEditDevice] = useState(null);
  const [deleteDevice, setDeleteDevice] = useState(null);
  const [equiposQuery, setEquiposQuery] = useState("");
  const [equiposTab, setEquiposTab] = useState("DISPONIBLE");
  const [equiposGen, setEquiposGen] = useState("all");
  const cp = owner.settlement_mode === "COST_PLUS_PROFIT_SHARE";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a, inv, pays, movs, cfg, ows] = await Promise.all([
        adminApi.get(`/admin/owners/${owner.id}/summary`),
        adminApi.get(`/admin/owners/${owner.id}/account`),
        adminApi.get(`/admin/inventory`).catch(() => ({ data: [] })),
        adminApi.get(`/admin/payments`, { params: { owner_id: owner.id } }).catch(() => ({ data: [] })),
        adminApi.get(`/admin/owners/${owner.id}/movements`).catch(() => ({ data: [] })),
        adminApi.get(`/config`).catch(() => ({ data: null })),
        adminApi.get(`/admin/owners`).catch(() => ({ data: [] })),
      ]);
      setSummary(s.data); setAccount(a.data);
      setUsedItems((inv.data || []).filter((i) => i.owner_id === owner.id));
      setPayments(pays.data || []); setMovements(movs.data || []);
      setCatalog(cfg.data); setOwnersList(ows.data || []);
    } catch (e) { toast.error("No se pudo cargar el consignador"); } finally { setLoading(false); }
  }, [owner.id]);
  const [photoCounts, setPhotoCounts] = useState({});
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const newIds = (summary?.items || []).map((i) => i.id);
    const usedIds = (usedItems || []).map((i) => i.id);
    const calls = [];
    if (newIds.length) calls.push(adminApi.get(`/admin/photos/counts?kind=new&ids=${newIds.join(",")}`));
    if (usedIds.length) calls.push(adminApi.get(`/admin/photos/counts?kind=used&ids=${usedIds.join(",")}`));
    if (!calls.length) { setPhotoCounts({}); return; }
    Promise.all(calls).then((rs) => { const m = {}; rs.forEach((r) => Object.assign(m, r.data || {})); setPhotoCounts(m); }).catch(() => {});
  }, [summary, usedItems]);

  const newItems = summary?.items || [];
  const allItems = [
    ...newItems.map((i) => ({ ...i, _capital: i.owner_cost, _src: "NUEVO" })),
    ...usedItems.map((i) => ({ ...i, _capital: i.acquisition_cost, _src: "USADO" })),
  ];
  const available = allItems.filter((i) => i.status === "DISPONIBLE");
  const soldItems = allItems.filter((i) => i.status === "VENDIDO");
  const CGEN = (m) => { const x = String(m || "").match(/1\d/); return x ? Number(x[0]) : 0; };
  const equiposGens = [...new Set(allItems.map((i) => CGEN(i.model)))].filter((n) => n > 0).sort((a, b) => b - a);
  const equiposTerms = normTxt(equiposQuery).split(/\s+/).filter(Boolean);
  const filteredItems = allItems.filter((i) => {
    if (equiposTab !== "TODOS" && i.status !== equiposTab) return false;
    if (equiposGen !== "all" && CGEN(i.model) !== Number(equiposGen)) return false;
    if (equiposTerms.length && !equiposTerms.every((t) => normTxt([i.model, i.capacity, i.color, i.imei, i.id, i._src, i.status].join(" ")).includes(t))) return false;
    return true;
  });
  const capitalMerch = available.reduce((t, i) => t + (Number(i._capital) || 0), 0);

  const confirmPay = async (p) => {
    const reference = window.prompt("Referencia / comprobante (opcional):", "") ?? "";
    try { await adminApi.post(`/admin/payments/${p.id}/confirm`, { reference }); toast.success("Pago confirmado"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo confirmar"); }
  };
  const cancelPay = async (p) => {
    const reason = window.prompt("Motivo de cancelación:", "") ?? "";
    try { await adminApi.post(`/admin/payments/${p.id}/cancel`, { reason }); toast.success("Solicitud cancelada"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo cancelar"); }
  };
  const attachReceipt = async (p, file) => {
    if (!validateReceipt(file)) return;
    try {
      await adminApi.post(`/admin/payments/${p.id}/receipt`, { data: await fileToData(file), filename: file.name });
      toast.success(p.has_receipt ? "Comprobante reemplazado" : "Comprobante adjuntado"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo adjuntar"); }
  };

  const approveSettlement = async (i) => {
    if (!window.confirm("¿Aprobar la propuesta de liquidación del consignador?")) return;
    try { await adminApi.post(`/admin/new-iphones/${i.id}/approve-settlement`); toast.success("Liquidación aprobada"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo aprobar"); }
  };
  const rejectSettlement = async (i) => {
    const reason = window.prompt("Motivo del rechazo / corrección solicitada:", "");
    if (reason == null) return;
    if (!reason.trim()) { toast.error("Indica un motivo"); return; }
    try { await adminApi.post(`/admin/new-iphones/${i.id}/reject-settlement`, { reason: reason.trim() }); toast.success("Propuesta rechazada"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo rechazar"); }
  };

  const SUBS = [["resumen", "Resumen"], ["movimientos", "Movimientos"], ["equipos", "Equipos"], ["ventas", "Ventas"], ["pagos", "Pagos"]];

  const movRows = movements;

  return (
    <div className="space-y-4" data-testid="cons-detail">
      <div className="flex items-center justify-between gap-2">
        <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900" data-testid="cons-back"><ArrowLeft className="w-4 h-4" /> Consignadores</button>
        <Button variant="outline" onClick={() => onEdit(owner)} className="h-9 rounded-lg text-xs" data-testid="cons-detail-edit">Editar</Button>
      </div>
      <div>
        <h3 className="text-xl font-bold text-zinc-900">{owner.name} <span className="text-zinc-400 font-mono text-sm">({owner.code})</span></h3>
        <span className="inline-block mt-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 text-[11px] font-semibold">{MODE_LABEL[owner.settlement_mode] || "Precio fijo"}{cp && owner.profit_share_pct != null ? ` · ${owner.profit_share_pct}%` : ""}</span>
      </div>

      <div className="flex gap-1 overflow-x-auto pb-1">
        {SUBS.map(([id, label]) => (
          <button key={id} onClick={() => setSub(id)} data-testid={`cons-sub-${id}`}
            className={`shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${sub === id ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>{label}</button>
        ))}
      </div>

      {loading ? <div className="py-10 text-center text-zinc-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div> : (
        <>
          {sub === "resumen" && (
            <div className="space-y-3" data-testid="cons-resumen">
              <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-5" data-testid="cons-saldo-principal">
                <p className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">Saldo por pagar al consignador</p>
                <p className="text-4xl sm:text-5xl font-bold text-emerald-800 mt-1 break-words" data-testid="cons-saldo-principal-value">{money(account?.disponible)}</p>
                <p className="text-[11px] text-emerald-700/80 mt-2 leading-snug">Dinero que PuntoCell debe al consignador por sus ventas aprobadas, menos los pagos registrados. No incluye el capital en mercancía.</p>
                {account?.pending_payments > 0 && <p className="text-[11px] font-medium text-amber-700 mt-1.5" data-testid="cons-saldo-pending">Pagos pendientes de confirmar: {money(account.pending_payments)}</p>}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Stat label="Total generado por ventas" value={money(account?.credits)} accent="text-emerald-700" />
                <Stat label="Total pagado" value={money(account?.confirmed_payments)} />
                <Stat label="Equipos disponibles" value={available.length} />
                <Stat label="Equipos vendidos" value={soldItems.length} />
                <Stat label="Capital en mercancía disponible" value={money(capitalMerch)} />
                <Stat label="Tipo de acuerdo" value={MODE_LABEL[owner.settlement_mode] || "Precio fijo"} />
                {cp && <Stat label="Ganancia PuntoCell" value={money(summary?.puntocell_profit)} />}
              </div>
            </div>
          )}

          {sub === "equipos" && (
            <div className="space-y-3" data-testid="cons-equipos">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
                <Input
                  value={equiposQuery}
                  onChange={(e) => setEquiposQuery(e.target.value)}
                  placeholder="Buscar modelo, capacidad, IMEI o ID..."
                  className="h-11 rounded-xl pl-9 pr-9"
                  data-testid="cons-equipos-search"
                />
                {equiposQuery && (
                  <button type="button" onClick={() => setEquiposQuery("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700" data-testid="cons-equipos-search-clear" aria-label="Limpiar búsqueda">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              {allItems.length === 0 ? (
                <p className="text-sm text-zinc-400 py-6 text-center">Sin equipos asignados.</p>
              ) : (
                <>
                  <div className="flex flex-wrap gap-1.5" data-testid="cons-equipos-status-tabs">
                    {[["DISPONIBLE", "Disponibles"], ["RESERVADO", "Reservados"], ["VENDIDO", "Vendidos"], ["RETIRADO", "Retirados"], ["TODOS", "Todos"]].map(([k, l]) => (
                      <button key={k} data-testid={`cons-equipos-status-${k}`} onClick={() => setEquiposTab(k)}
                        className={`h-8 px-3 rounded-full text-xs font-medium transition-colors ${equiposTab === k ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>
                        {l} <span className={equiposTab === k ? "text-white/70" : "text-zinc-400"}>({k === "TODOS" ? allItems.length : allItems.filter((i) => i.status === k).length})</span>
                      </button>
                    ))}
                  </div>
                  {equiposGens.length > 0 && (
                    <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-1" data-testid="cons-equipos-gen-tabs">
                      <button data-testid="cons-equipos-gen-all" onClick={() => setEquiposGen("all")}
                        className={`shrink-0 h-8 px-3 rounded-full text-xs font-medium transition-colors ${equiposGen === "all" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>Todos</button>
                      {equiposGens.map((g) => (
                        <button key={g} data-testid={`cons-equipos-gen-${g}`} onClick={() => setEquiposGen(String(g))}
                          className={`shrink-0 h-8 px-3 rounded-full text-xs font-medium transition-colors ${equiposGen === String(g) ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>iPhone {g}</button>
                      ))}
                    </div>
                  )}
                  <p className="text-[11px] text-zinc-500" data-testid="cons-equipos-count">Mostrando {filteredItems.length} de {allItems.length} equipos</p>
                  {filteredItems.length === 0 ? (
                    <p className="text-sm text-zinc-400 py-6 text-center" data-testid="cons-equipos-empty">No encontramos equipos con esa búsqueda.</p>
                  ) : filteredItems.map((i) => (
                    <div key={i.id} className="rounded-xl border border-zinc-200 bg-white p-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`cons-equipo-${i.id}`}>
                      <div className="min-w-0 space-y-0.5">
                        <div className="flex items-start gap-2">
                          <p className="text-sm font-semibold text-zinc-900 break-words flex-1 min-w-0">{i.model} <span className="font-normal text-zinc-400">{i.capacity}</span></p>
                          <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${STATUS_BADGE[i.status] || "bg-zinc-100"}`}>{i.status}</span>
                        </div>
                        <p className="text-[11px] text-zinc-500 break-words">{i.id} · {i._src} · 👤 {i.owner_name || owner.name} · IMEI {i.imei || "—"}{i.color ? ` · ${i.color}` : ""}</p>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 text-[10px] font-semibold" data-testid={`cons-equipo-mode-${i.id}`}>{modeLabel(i.settlement_mode || owner.settlement_mode)}{(i.settlement_mode || owner.settlement_mode) === MODE_CP && (i.profit_share_pct ?? owner.profit_share_pct) != null ? ` · ${i.profit_share_pct ?? owner.profit_share_pct}%` : ""}</span>
                          {(i.settlement_mode || owner.settlement_mode) === MODE_POST
                            ? <span className="text-[10px] text-zinc-500">Se acuerda tras vender</span>
                            : (i.owner_cost ?? i._capital) != null ? <span className="text-[10px] text-zinc-500">{(i.settlement_mode || owner.settlement_mode) === MODE_CP ? "Capital" : "Acordado"}: {money(i.owner_cost ?? i._capital)}</span> : null}
                          {i.status === "VENDIDO" && (i.sale_price || i.sale?.sale_price) ? <span className="text-[10px] text-zinc-500">Venta: {money(i.sale_price || i.sale?.sale_price)}</span> : null}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 sm:flex sm:items-center sm:gap-2 sm:shrink-0">
                        <DevicePhotos scope="admin" kind={i._src === "USADO" ? "used" : "new"} deviceId={i.id} canUpload count={photoCounts[i.id] || 0} label="Fotos reales" buttonClassName="w-full sm:w-auto justify-center" />
                        <Button variant="outline" size="sm" onClick={() => setEditDevice(i)} className="h-8 w-full sm:w-auto rounded-lg text-[11px]" data-testid={`cons-equipo-edit-${i.id}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Editar</Button>
                        <Button variant="outline" size="sm" onClick={() => setDeleteDevice(i)} className="h-8 w-full sm:w-auto rounded-lg text-[11px] text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`cons-equipo-delete-${i.id}`}><Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar</Button>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {sub === "ventas" && (
            <div className="space-y-2" data-testid="cons-ventas">
              {soldItems.length === 0 && <p className="text-sm text-zinc-400 py-6 text-center">Sin ventas registradas.</p>}
              {soldItems.map((i) => {
                const s = i.sale || {};
                const sold = s.actual_sale_price ?? s.sale_price ?? i.sale_price;
                const ownerCost = s.owner_purchase_cost ?? s.owner_cost ?? i._capital;
                const isCp = s.settlement_mode === "COST_PLUS_PROFIT_SHARE";
                const isPost = s.settlement_mode === "POST_SALE_SETTLEMENT";
                const pc = s.puntocell_profit_share != null ? s.puntocell_profit_share : (s.gross_profit != null && s.owner_profit_share != null ? Math.round((s.gross_profit - s.owner_profit_share) * 100) / 100 : null);
                const saleDate = (s.date || s.sold_at || s.sale_date || i.exit_date || "").slice(0, 10);
                const appr = s.approval_status;
                const hasSale = !!i.sale;
                const modeLbl = isPost ? "Liquidación posterior" : isCp ? "Capital + participación" : "Precio fijo";
                const pubPrice = i.sale_price ?? s.listed_price ?? s.sale_price;
                const liq = isPost ? (s.post_sale_status === "LIQUIDADO" ? "Liquidada" : s.post_sale_status === "PROPOSED" ? "Propuesta pendiente" : "Pendiente de acordar") : (s.owner_settlement != null ? "Liquidación lista" : "Pendiente");
                return (
                  <div key={i.id} className="rounded-xl border border-zinc-200 bg-white p-3 space-y-1.5 text-xs" data-testid={`cons-venta-${i.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-zinc-900 truncate">{i.model} {i.capacity}{i.color ? ` · ${i.color}` : ""}</p>
                        <p className="text-[11px] text-zinc-400 font-mono truncate">{i.id}{i.imei ? ` · IMEI ${i.imei}` : ""}</p>
                      </div>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${i._src === "USADO" ? "bg-orange-100 text-orange-700" : "bg-blue-100 text-blue-700"}`}>{i._src}</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {hasSale && <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-zinc-600 text-[10px] font-medium">{modeLbl}</span>}
                      {saleDate && <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-zinc-500 text-[10px]">{saleDate}</span>}
                      {hasSale && (appr === "PENDIENTE_APROBACION" ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-amber-700 text-[10px] font-semibold">Pendiente aprob.</span>
                        : appr === "RECHAZADA" ? <span className="rounded-full bg-rose-100 px-2 py-0.5 text-rose-700 text-[10px] font-semibold">Rechazada</span>
                        : <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-emerald-700 text-[10px] font-semibold">Aprobada</span>)}
                      {hasSale && <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-zinc-500 text-[10px]">{liq}</span>}
                      {!hasSale && <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-zinc-500 text-[10px]">Salida de inventario</span>}
                    </div>
                    {!isPost && pubPrice != null && <div className="flex justify-between"><span className="text-zinc-500">Precio publicado</span><span className="font-medium">{money(pubPrice)}</span></div>}
                    {isPost ? (
                      <>
                        {s.declared_owner_cost != null && <div className="flex justify-between"><span className="text-zinc-500">Costo declarado</span><span className="font-medium">{money(s.declared_owner_cost)}</span></div>}
                        <div className="flex justify-between"><span className="text-zinc-500">Precio publicado</span><span className="font-medium">{money(i.sale_price ?? s.sale_price)}</span></div>
                        <div className="flex justify-between"><span className="text-zinc-500">Precio real vendido</span><span className="font-medium">{money(sold)}</span></div>
                        {s.post_sale_status === "LIQUIDADO" ? (
                          <>
                            <div className="flex justify-between"><span className="text-zinc-500">Monto consignador</span><span className="font-medium text-emerald-700">{money(s.owner_settlement)}</span></div>
                            <div className="flex justify-between"><span className="text-zinc-500">Ganancia PuntoCell</span><span className="font-medium">{money(s.puntocell_profit_share)}</span></div>
                            <p className="text-[11px] font-semibold text-emerald-700">LIQUIDADO</p>
                          </>
                        ) : s.post_sale_status === "PROPOSED" ? (
                          <>
                            <div className="flex justify-between"><span className="text-zinc-500">Propuesta del consignador</span><span className="font-medium text-indigo-700">{money(s.proposed_owner_amount)}</span></div>
                            <div className="flex justify-between"><span className="text-zinc-500">Para PuntoCell</span><span className="font-medium">{money(s.proposed_pc)}</span></div>
                            {s.proposed_note && <p className="text-[11px] text-zinc-500">Nota: {s.proposed_note}</p>}
                            <p className="text-[11px] font-semibold text-amber-600">Pendiente de aprobación</p>
                            {i._src === "NUEVO" && <div className="flex gap-2 mt-1">
                              <button onClick={() => approveSettlement(i)} className="h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold" data-testid={`cons-approve-${i.id}`}>Aprobar</button>
                              <button onClick={() => rejectSettlement(i)} className="h-8 px-3 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-[11px] font-semibold" data-testid={`cons-reject-${i.id}`}>Rechazar</button>
                            </div>}
                          </>
                        ) : (
                          <p className="text-[11px] font-semibold text-amber-600" data-testid={`cons-venta-pending-${i.id}`}>Liquidación posterior · Esperando propuesta del consignador</p>
                        )}
                      </>
                    ) : isCp ? (
                      <>
                        <div className="flex justify-between"><span className="text-zinc-500">Precio de venta</span><span className="font-medium">{money(sold)}</span></div>
                        <div className="flex justify-between"><span className="text-zinc-500">Capital</span><span className="font-medium">{money(ownerCost)}</span></div>
                        <div className="flex justify-between"><span className="text-zinc-500">Utilidad</span><span className="font-medium">{money(s.gross_profit)}</span></div>
                        <div className="flex justify-between"><span className="text-zinc-500">% consignador</span><span className="font-medium">{s.profit_share_pct}%</span></div>
                        <div className="flex justify-between"><span className="text-zinc-500">Ganancia consignador</span><span className="font-medium text-emerald-700">{money(s.owner_profit_share)}</span></div>
                        <div className="flex justify-between"><span className="text-zinc-500">Ganancia PuntoCell</span><span className="font-medium">{money(pc)}</span></div>
                        <div className="flex justify-between border-t border-zinc-200 pt-1 mt-1"><span className="font-semibold text-zinc-700">Total generado para consignador</span><span className="font-bold">{money(s.owner_settlement)}</span></div>
                      </>
                    ) : (
                      <>
                        <div className="flex justify-between"><span className="text-zinc-500">Precio fijo del consignador</span><span className="font-medium">{money(ownerCost)}</span></div>
                        <div className="flex justify-between border-t border-zinc-200 pt-1 mt-1"><span className="font-semibold text-zinc-700">Total generado</span><span className="font-bold">{money(s.owner_settlement ?? ownerCost)}</span></div>
                      </>
                    )}
                    {i.sale && <button onClick={() => setCorrecting(i)} className="mt-1 text-[11px] text-indigo-600 hover:text-indigo-700 font-semibold" data-testid={`cons-correct-${i.id}`}>Corregir venta</button>}
                  </div>
                );
              })}
            </div>
          )}

          {sub === "pagos" && (
            <div className="space-y-3" data-testid="cons-pagos">
              <Button onClick={() => setPayOpen(true)} className="h-10 rounded-xl bg-zinc-900 hover:bg-zinc-800 w-full" data-testid="cons-pay-new"><Plus className="w-4 h-4 mr-1" /> Registrar pago</Button>
              <p className="text-xs font-semibold text-zinc-500 pt-1">Historial de pagos</p>
              {payments.length === 0 && <p className="text-sm text-zinc-400 py-6 text-center">Sin pagos registrados.</p>}
              {payments.map((p) => <PayRow key={p.id} p={p} onConfirm={confirmPay} onCancel={cancelPay} onAttach={attachReceipt} />)}
            </div>
          )}

          {sub === "movimientos" && (
            <div className="space-y-3" data-testid="cons-movimientos">
              <div className="grid grid-cols-3 gap-2" data-testid="cons-mov-summary">
                <Stat label="Total generado" value={money(account?.credits)} accent="text-emerald-700" />
                <Stat label="Total pagado" value={money(account?.confirmed_payments)} />
                <Stat label="Saldo por pagar" value={money(account?.disponible)} accent="text-emerald-700" />
              </div>
              <div className="space-y-1">
                {movRows.length === 0 && <p className="text-sm text-zinc-400 py-6 text-center">Sin movimientos.</p>}
                {movRows.map((m, idx) => (
                  <div key={idx} className="rounded-xl border border-zinc-200 bg-white p-3 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm text-zinc-800 truncate">{m.label}</p>
                      <p className="text-[11px] text-zinc-400">{fdate(m.date)} · {m.reference || "—"}</p>
                    </div>
                    <p className={`text-sm font-semibold shrink-0 ${m.sign === "+" ? "text-emerald-700" : "text-rose-600"}`}>{m.sign}{money(m.amount)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      {payOpen && <PaymentDialog owner={owner} disponible={account?.disponible || 0} onClose={() => setPayOpen(false)} onSaved={() => { setPayOpen(false); load(); }} />}
      {correcting && <CorrectSaleDialog item={correcting} isUsed={correcting._src === "USADO"} onClose={() => setCorrecting(null)} onSaved={() => { setCorrecting(null); load(); }} />}
      {settling && <PostSaleSettleDialog item={settling} onClose={() => setSettling(null)} onSaved={() => { setSettling(null); load(); }} />}
      {editDevice && editDevice._src === "NUEVO" && (
        <DeviceDialog open onOpenChange={(o) => { if (!o) setEditDevice(null); }} catalog={catalog} owners={ownersList} editing={editDevice} onSaved={() => { setEditDevice(null); load(); }} />
      )}
      {editDevice && editDevice._src === "USADO" && (
        <EditInventoryDialog item={editDevice} catalog={catalog} owners={ownersList} onClose={() => setEditDevice(null)} onSaved={() => { setEditDevice(null); load(); }} />
      )}
      {deleteDevice && <DeleteDeviceDialog kind={deleteDevice._src === "NUEVO" ? "new" : "used"} item={{ ...deleteDevice, owner_name: deleteDevice.owner_name || owner.name }} onClose={() => setDeleteDevice(null)} onDeleted={() => { setDeleteDevice(null); load(); }} />}
    </div>
  );
}

function PaymentDialog({ owner, disponible, onClose, onSaved }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("EFECTIVO");
  const [note, setNote] = useState("");
  const [receiptFile, setReceiptFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const amt = Number(amount);
    if (!(amt > 0)) { toast.error("Monto inválido"); return; }
    if (amt > disponible + 1e-6) { toast.error(`Excede el disponible (${money(disponible)})`); return; }
    setSaving(true);
    try {
      const r = await adminApi.post("/admin/payments", { owner_id: owner.id, method, amount: amt, note: note || null });
      if (receiptFile) {
        try { await adminApi.post(`/admin/payments/${r.data.id}/receipt`, { data: await fileToData(receiptFile), filename: receiptFile.name }); }
        catch { toast.error("Pago creado, pero el comprobante no se pudo adjuntar"); }
      }
      toast.success("Pago registrado (pendiente de confirmar)"); onSaved?.();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo registrar"); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="cons-pay-dialog">
        <DialogHeader><DialogTitle>Registrar pago · {owner.name}</DialogTitle>
          <DialogDescription>Disponible: {money(disponible)}. El pago queda PENDIENTE hasta confirmarlo.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Input type="number" min="0" placeholder="Monto" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-11 rounded-xl" data-testid="cons-pay-amount" />
          <select value={method} onChange={(e) => setMethod(e.target.value)} className="h-11 w-full rounded-xl border border-zinc-200 px-3 text-sm" data-testid="cons-pay-method">
            {["EFECTIVO", "ZELLE", "WIRE", "USDT", "OTRO"].map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <Input placeholder="Nota (opcional)" value={note} onChange={(e) => setNote(e.target.value)} className="h-11 rounded-xl" data-testid="cons-pay-note" />
          <label className="flex items-center gap-2 text-xs text-zinc-600 cursor-pointer rounded-xl border border-dashed border-zinc-300 px-3 py-2.5 hover:bg-zinc-50">
            <Upload className="w-4 h-4" /> {receiptFile ? `Comprobante: ${receiptFile.name}` : "Adjuntar comprobante (opcional)"}
            <input type="file" accept={RECEIPT_ACCEPT} data-testid="cons-pay-receipt" onChange={(e) => { const f = e.target.files?.[0]; if (f && validateReceipt(f)) setReceiptFile(f); e.target.value = ""; }} className="hidden" />
          </label>
          <Button onClick={save} disabled={saving} className="h-11 w-full rounded-xl bg-zinc-900 hover:bg-zinc-800" data-testid="cons-pay-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Registrar"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ConsignadoresTab() {
  const [owners, setOwners] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ownersRes, invRes] = await Promise.all([
        adminApi.get("/admin/owners"),
        adminApi.get("/admin/inventory").catch(() => ({ data: [] })),
      ]);
      const list = ownersRes.data || [];
      setOwners(list);
      const usedByOwner = {};
      (invRes.data || []).forEach((i) => {
        if (!i.owner_id) return;
        (usedByOwner[i.owner_id] = usedByOwner[i.owner_id] || { available: 0, sold: 0 });
        if (i.status === "DISPONIBLE") usedByOwner[i.owner_id].available += 1;
        else if (i.status === "VENDIDO") usedByOwner[i.owner_id].sold += 1;
      });
      const entries = await Promise.all(list.map(async (o) => {
        try {
          const [s, a] = await Promise.all([
            adminApi.get(`/admin/owners/${o.id}/summary`),
            adminApi.get(`/admin/owners/${o.id}/account`).catch(() => ({ data: {} })),
          ]);
          const u = usedByOwner[o.id] || { available: 0, sold: 0 };
          return [o.id, { available: (s.data?.counts?.available ?? 0) + u.available, sold: (s.data?.counts?.sold ?? 0) + u.sold, disponible: a.data?.disponible ?? 0 }];
        } catch { return [o.id, { available: 0, sold: 0, disponible: 0 }]; }
      }));
      setMeta(Object.fromEntries(entries));
    } catch (e) { toast.error("No se pudieron cargar los consignadores"); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (selected) {
    const fresh = owners.find((o) => o.id === selected.id) || selected;
    return (
      <>
        <OwnerDetail owner={fresh} onBack={() => { setSelected(null); load(); }} onEdit={(o) => setForm(o)} />
        {form && <OwnerFormDialog owner={form} onClose={() => setForm(null)} onSaved={load} />}
      </>
    );
  }

  return (
    <div className="space-y-4" data-testid="consignadores-tab">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="text-lg font-bold text-zinc-900 flex items-center gap-2"><Users className="w-5 h-5 text-indigo-600" /> Consignadores</h3>
          <p className="text-xs text-zinc-500">Administra a los dueños de mercancía, su acuerdo y su cuenta.</p>
        </div>
        <Button onClick={() => setForm({})} className="h-10 rounded-xl bg-zinc-900 hover:bg-zinc-800" data-testid="cons-new"><Plus className="w-4 h-4 mr-1" /> Nuevo</Button>
      </div>

      {loading ? <div className="py-10 text-center text-zinc-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div> : (
        <div className="grid gap-2">
          {owners.length === 0 && <p className="text-sm text-zinc-400 py-6 text-center">Sin consignadores.</p>}
          {owners.map((o) => {
            const m = meta[o.id] || {};
            const cp = o.settlement_mode === "COST_PLUS_PROFIT_SHARE";
            return (
              <button key={o.id} onClick={() => setSelected(o)} data-testid={`cons-card-${o.id}`}
                className="text-left rounded-2xl border border-zinc-200 bg-white p-4 hover:border-zinc-300 transition-colors">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-zinc-900 truncate">{o.name} <span className="text-zinc-400 font-mono text-xs">({o.code})</span></p>
                    <span className="inline-block mt-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 text-[10px] font-semibold">{MODE_LABEL[o.settlement_mode] || "Precio fijo"}{cp && o.profit_share_pct != null ? ` · ${o.profit_share_pct}%` : ""}{o.active === false ? " · inactivo" : ""}</span>
                  </div>
                  <ArrowLeft className="w-4 h-4 text-zinc-300 rotate-180 shrink-0" />
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                  <div><p className="text-[10px] text-zinc-400 uppercase flex items-center justify-center gap-1"><Boxes className="w-3 h-3" />Disp.</p><p className="text-sm font-bold text-zinc-900">{m.available ?? "—"}</p></div>
                  <div><p className="text-[10px] text-zinc-400 uppercase flex items-center justify-center gap-1"><ShoppingCart className="w-3 h-3" />Vend.</p><p className="text-sm font-bold text-zinc-900">{m.sold ?? "—"}</p></div>
                  <div><p className="text-[10px] text-zinc-400 uppercase flex items-center justify-center gap-1"><Wallet className="w-3 h-3" />Saldo</p><p className="text-sm font-bold text-emerald-700">{money(m.disponible)}</p></div>
                </div>
              </button>
            );
          })}
        </div>
      )}
      {form && <OwnerFormDialog owner={form.id ? form : null} onClose={() => setForm(null)} onSaved={load} />}
    </div>
  );
}
